import React, { useCallback, useState, useMemo, useRef, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { usePagination, useTable, useBlockLayout } from 'react-table'
import classNames from 'classnames'
import InviteUserModal from './InviteUserModal'
import useOutsideClickListener from 'LegacyUtils/hooks/useOutsideClickListener'
import { v4 } from 'uuid'
import toast from 'react-hot-toast'

import { canDeleteUsers, canEditUsers, canInviteUsers, getCurrentUser } from 'Selectors/users'
import EditUserModal from './EditUserModal'
import { deleteUser, deactivateUser, activateUser } from './service'

const Options = ({ user, refreshPage }) => {
  const { id, is_active, is_deleted, is_verified, name, invitation_token } = user

  const currentUser = useSelector(getCurrentUser)
  const isCurrentUser = currentUser.id === id

  const [show, setShow] = useState(false)
  const ref = useRef()

  const [showEditModal, setShowEditModal] = useState(false)

  const closeEditUserModal = useCallback(() => {
    setShowEditModal(false)
  }, [])

  const closeHandler = useCallback(() => setShow(false), [setShow])

  useOutsideClickListener(ref, closeHandler, show)

  const onDeleteClick = useCallback(
    async (e) => {
      e.preventDefault()
      const result = confirm(`Are you sure you want to delete user '${name}'? This action cannot be undone.`)
      if (!result) {
        return
      }
      const deleteToastId = v4()
      toast.loading('Deleting user...', {
        id: deleteToastId
      })
      try {
        await deleteUser(id)
        await refreshPage()
        toast.success('Deleted user', {
          id: deleteToastId
        })
      } catch (err) {
        toast.error('Error deleting user', {
          id: deleteToastId
        })
        console.error('Error while deleting user', err)
      }
    },
    [id, name, refreshPage]
  )

  const onDeactivateClick = useCallback(
    async (e) => {
      e.preventDefault()

      const result = confirm(`Are you sure you want to deactivate user '${name}'?`)
      if (!result) {
        return
      }
      const deActivateToastId = v4()
      toast.loading('Deactivating user...', {
        id: deActivateToastId
      })
      try {
        await deactivateUser(id)
        await refreshPage()
        toast.success('Deactivated user', {
          id: deActivateToastId
        })
      } catch (err) {
        toast.error('Error deactivating user', {
          id: deActivateToastId
        })
        console.error('Error while deactivating user', err)
      }
    },
    [refreshPage, id, name]
  )

  const onActivateClick = useCallback(
    async (e) => {
      const activateToastId = v4()

      e.preventDefault()
      const result = confirm(`Are you sure you want to activate user '${name}'?`)
      if (!result) {
        return
      }

      toast.loading('Activating user...', {
        id: activateToastId
      })
      try {
        await activateUser(id)
        await refreshPage()
        toast.success('Activated user', {
          id: activateToastId
        })
      } catch (err) {
        toast.error('Error activating user', {
          id: activateToastId
        })
        console.error('Error while activating user', err)
      }
    },
    [refreshPage, id, name]
  )

  const defaultClick = useCallback(
    (e) => {
      e.preventDefault()
      setShow(!show)
    },
    [show]
  )

  const onEditClick = useCallback((e) => {
    e.preventDefault()
    setShowEditModal(true)
  }, [])

  const canDeleteUser = useSelector(canDeleteUsers)
  const canEditUser = useSelector(canEditUsers)

  const hasAnyPermission = canEditUser || canDeleteUser

  const editUserPermission = canEditUser && is_active && !invitation_token

  const canShowActiveOption = !is_active && is_verified

  return (
    <React.Fragment>
      <div
        className={classNames('options-dots', { disabled: isCurrentUser || !hasAnyPermission })}
        onClick={defaultClick}
      >
        {show && !is_deleted && (
          <div className='dropdown-menu' ref={ref}>
            <span className='dropdown-menu-arrow' />
            {editUserPermission && (
              <a className='dropdown-item' onClick={onEditClick}>
                Edit
              </a>
            )}
            {canDeleteUser && (
              <React.Fragment>
                {is_active && (
                  <a className='dropdown-item' onClick={onDeactivateClick}>
                    Deactivate
                  </a>
                )}
                {canShowActiveOption && (
                  <a className='dropdown-item' onClick={onActivateClick}>
                    Activate
                  </a>
                )}
                <a className='dropdown-item' onClick={onDeleteClick}>
                  Delete
                </a>
              </React.Fragment>
            )}
          </div>
        )}
        {show && is_deleted && (
          <div className='dropdown-menu' ref={ref}>
            <span className='dropdown-menu-arrow' />
            <a className='dropdown-item disabled'>User Deleted</a>
          </div>
        )}
      </div>
      <EditUserModal isOpen={showEditModal} onClose={closeEditUserModal} user={user} />
    </React.Fragment>
  )
}

const UsersList = (props) => {
  const { users, pageCount, pageSize, currentPage, setCurrentPage, refreshPage } = props

  const columns = useMemo(() => {
    return [
      {
        Header: 'NAME',
        accessor: 'name'
      },
      {
        Header: 'EMAIL',
        accessor: 'email'
      },
      {
        Header: 'Status',
        accessor: (row) => row,
        Cell: ({ cell: { value } }) => {
          if (!value.is_verified) {
            return 'Invited'
          } else if (value.is_verified && value.is_active) {
            return 'Active'
          } else if (!value.is_active) {
            return 'Inactive'
          }
        }
      }
    ]
  }, [])

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    nextPage,
    previousPage,
    state: { pageIndex }
  } = useTable(
    {
      columns,
      data: users,
      initialState: { pageIndex: currentPage - 1, pageSize }, // Pass our hoisted table state
      manualPagination: true, // Tell the usePagination
      pageCount
    },
    useBlockLayout,
    usePagination
  )

  useEffect(() => {
    setCurrentPage(pageIndex + 1)
  }, [pageIndex, setCurrentPage])

  const canInvite = useSelector(canInviteUsers)

  const [showInviteModal, setShowInviteModal] = useState(false)

  const onShowModalClick = useCallback(() => {
    setShowInviteModal(true)
  }, [])

  const hideModal = useCallback(() => {
    setShowInviteModal(false)
  }, [])

  return (
    <React.Fragment>
      <div className='page-top-bar'>
        <div className='title'>Users</div>
        <div className='right'>
          {canInvite && (
            <button className='button-primary mr-2' onClick={onShowModalClick}>
              Invite User
            </button>
          )}
          {(canPreviousPage || canNextPage) && (
            <div className='pagination'>
              <button
                className='previous-button pagination-button mr-1'
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                ‹
              </button>{' '}
              <button className='next-button pagination-button' onClick={() => nextPage()} disabled={!canNextPage}>
                ›
              </button>{' '}
            </div>
          )}
        </div>
      </div>
      {page.length > 0 && (
        <div>
          <div className='list-container'>
            <div {...getTableProps()} className='table'>
              <div className='thead'>
                {headerGroups.map((headerGroup) => {
                  const { key } = headerGroup.getHeaderGroupProps()
                  return (
                    <div className='tr' key={key} {...headerGroup.getHeaderGroupProps()}>
                      {headerGroup.headers.map((column) => {
                        const { key } = column.getHeaderProps()
                        return (
                          <div className='th' {...column.getHeaderProps()} key={key}>
                            {column.render('Header')}
                          </div>
                        )
                      })}
                      <div className='th options-column' />
                    </div>
                  )
                })}
              </div>
              <div className='tbody' {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row)
                  const { key, ...rowProps } = row.getRowProps()
                  const { is_active, is_deleted } = row.original

                  if (is_deleted) {
                    return null
                  }

                  return (
                    <div
                      {...rowProps}
                      key={key}
                      className={classNames('pointer tr', { 'text-muted': !is_active || is_deleted })}
                    >
                      {row.cells.map((cell) => {
                        const { key, ...cellProps } = cell.getCellProps()
                        return (
                          <div className='td' {...cellProps} key={key}>
                            {cell.render('Cell')}
                          </div>
                        )
                      })}
                      <div className='options td'>
                        <Options user={row.original} refreshPage={refreshPage} />
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        </div>
      )}
      {!page.length && (
        <div className='empty-state'>
          <div className='empty-state-content'>
            <h1>Invite a new user</h1>
            <p>Invite your colleagues to collaborate on building Chatbot flows and content</p>
            <button onClick={props.onShowModalClick} className='button-primary mb-3'>
              Invite a new User
            </button>
          </div>
        </div>
      )}
      {showInviteModal && <InviteUserModal isOpen={showInviteModal} hide={hideModal} refreshPage={refreshPage} />}
    </React.Fragment>
  )
}

export default UsersList
